import {
  Children,
  Fragment,
  isValidElement,
  useId,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { Glyph, type GlyphMeaning } from "@apf/design-system/glyphs";
import { useCopy, useFramework } from "../framework";
import { usePageTitle, useRevealOnce } from "../pages/support";

const humanizeAcronyms = new Set([
  "AI",
  "API",
  "CLM",
  "DOCX",
  "ERP",
  "ESG",
  "ISO",
  "KPI",
  "P2P",
  "RFI",
  "RFP",
  "RFQ",
  "S2P",
  "SLA",
  "SOC",
  "TPRM",
  "XLSX"
]);

export function PageHeader({
  eyebrow,
  title,
  description,
  meta
}: {
  eyebrow: string;
  title: string;
  description: string;
  meta?: ReactNode;
}) {
  usePageTitle(title);
  return (
    <header className="apf-page-header">
      <div>
        <p className="apf-page-header__eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="apf-page-header__description">{description}</p>
      </div>
      {meta ? <div className="apf-page-header__meta">{meta}</div> : null}
    </header>
  );
}

export function SectionHeading({ title, description, action, glyph, id }: {
  title: string;
  description?: string;
  action?: ReactNode;
  /** Content-class glyph rendered beside the visible title, never instead of it. */
  glyph?: GlyphMeaning;
  /** Optional stable heading ID for section labelling and deep-link semantics. */
  id?: string;
}) {
  return (
    <div className="apf-section-heading">
      <div>
        <h2 id={id}>
          {glyph ? <Glyph meaning={glyph} size={20} className="apf-heading-glyph" /> : null}
          {title}
        </h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

function normalizeStatusToken(value: string): string {
  return value.toLowerCase().replaceAll("_", "-").replaceAll(" ", "-");
}

/**
 * Status wording resolved through the governed status display metadata
 * (snapshot.content.businessModuleStatusDisplay) whenever the value is an implementation
 * status token, so `reference` renders as "Reference build", never as a bare
 * humanized token. Other status vocabularies fall back to `label` or humanize.
 *
 * On editorial surfaces the state is labelled text with a leading state
 * square. The mark is never underlined, so it cannot read as a link.
 */
export function StatusMark({ value, label }: { value: string; label?: string }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const normalized = normalizeStatusToken(value);
  const display = snapshot.content.businessModuleStatusDisplay.find((status) => status.id === normalized);
  const text = label ?? (display ? copy(display.chipKey) : humanize(value));
  return <span className={`apf-status-mark apf-status-mark--${normalized}`}>{text}</span>;
}

/** One-line legend for the Workblock status taxonomy, shown where the marks first appear on a page. */
export function StatusLegend() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const statuses = snapshot.content.businessModuleStatusDisplay;
  if (!statuses.length) return null;
  return (
    <p className="apf-status-legend">
      {statuses.map((status) => (
        <span className="apf-status-legend__item" key={status.id}>
          <strong>{copy(status.chipKey)}</strong>: {copy(status.legendKey)}
        </span>
      ))}
    </p>
  );
}

export function humanize(value: string): string {
  return value
    .trim()
    .replace(/[_-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      const uppercase = word.toUpperCase();
      if (humanizeAcronyms.has(uppercase)) return uppercase;
      const lowercase = word.toLowerCase();
      return index === 0 ? `${lowercase.charAt(0).toUpperCase()}${lowercase.slice(1)}` : lowercase;
    })
    .join(" ");
}

export function EmptyState({
  title,
  description,
  action,
  titleAs: TitleTag = "h2",
}: {
  title: string;
  description: string;
  action?: ReactNode;
  /** "h1" only when a collection-zero view owns the page's heading hierarchy. */
  titleAs?: "h1" | "h2";
}) {
  return (
    <div className="apf-empty-state apf-surface">
      <div className="apf-state-message">
        <TitleTag>{title}</TitleTag>
        <p>{description}</p>
        {action ? <div className="apf-empty-state__action">{action}</div> : null}
      </div>
    </div>
  );
}

/** One counted reading of what the framework still holds beside an absence. */
export type AbsenceHolding = {
  label: ReactNode;
  count: number;
};

export interface AbsenceBlockProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  /** The kind of absence, stated in the framework register. */
  eyebrow: ReactNode;
  /** One sentence naming exactly what is absent. */
  title: ReactNode;
  /** One sentence explaining the absence in the framework's own terms. */
  support: ReactNode;
  /** Up to three counted readings of what the framework does hold. */
  holds?: AbsenceHolding[];
  /** One or two destination links that give the reader a way on. */
  routes?: ReactNode;
  /** `h1` is reserved for a hosted missing-page route; nested blocks use h2-h4. */
  headingLevel?: 1 | 2 | 3 | 4;
}

const ABSENCE_HOLDING_CEILING = 3;
const ABSENCE_ROUTE_CEILING = 2;

type AbsenceRouteElementProps = {
  children?: ReactNode;
  href?: unknown;
  to?: unknown;
};

function collectAbsenceRouteElements(
  routes: ReactNode,
  elements: ReactElement<AbsenceRouteElementProps>[] = [],
): ReactElement<AbsenceRouteElementProps>[] {
  Children.forEach(routes, (route) => {
    if (route === null || route === undefined || typeof route === "boolean") return;
    if (typeof route === "string" && route.trim().length === 0) return;
    if (!isValidElement<AbsenceRouteElementProps>(route)) {
      throw new Error(
        "AbsenceBlock: routes accept only one or two destination links; non-link content is not permitted.",
      );
    }
    if (route.type === Fragment) {
      collectAbsenceRouteElements(route.props.children, elements);
      return;
    }
    elements.push(route);
  });
  return elements;
}

function assertAbsenceRoutes(routes: ReactNode): void {
  const elements = collectAbsenceRouteElements(routes);
  if (elements.length < 1 || elements.length > ABSENCE_ROUTE_CEILING) {
    throw new Error(
      `AbsenceBlock: routes must contain one or two destination links; received ${elements.length}.`,
    );
  }

  for (const route of elements) {
    if (route.type === "a") {
      if (typeof route.props.href === "string" && route.props.href.trim().length > 0) continue;
      throw new Error(
        "AbsenceBlock: every anchor route needs a non-empty href destination.",
      );
    }
    if (route.type === Link) {
      const destination = route.props.to;
      if (
        (typeof destination === "string" && destination.trim().length > 0) ||
        (typeof destination === "object" && destination !== null)
      ) {
        continue;
      }
      throw new Error(
        "AbsenceBlock: every router route needs a non-empty to destination.",
      );
    }
    throw new Error(
      "AbsenceBlock: routes accept only anchor or router-link elements; buttons and non-link elements are not permitted.",
    );
  }
}

/**
 * The framework-register surface for a missing record, empty section or
 * address that names no page. Collection zero states continue to use
 * EmptyState; AbsenceBlock states what is missing, what still holds and the
 * route back without drawing a box or presenting an action button.
 */
export function AbsenceBlock({
  eyebrow,
  title,
  support,
  holds,
  routes,
  headingLevel = 3,
  className,
  ...sectionProps
}: AbsenceBlockProps) {
  const titleId = useId();
  const Heading = `h${headingLevel}` as "h1" | "h2" | "h3" | "h4";
  /* A zero holding is not something that still holds; it is dropped at this
   * API for the same reason FactRow and FactBand drop zero readings. */
  const readings = (holds ?? []).filter((reading) => !isZeroFact(reading.count));
  /* A page-level absence names the page; section-level absences leave the
   * document title to the page that hosts them. */
  usePageTitle(
    typeof title === "string" ? title : undefined,
    headingLevel === 1 && typeof title === "string",
  );

  if (routes) {
    assertAbsenceRoutes(routes);
  } else {
    console.warn(
      "AbsenceBlock: no routes were supplied. A whole missing page may carry its way back in the page trail; every other absence needs one or two destination links."
    );
  }
  if (readings.length > ABSENCE_HOLDING_CEILING) {
    console.warn(
      `AbsenceBlock: ${readings.length} holdings were supplied; the contract allows at most ${ABSENCE_HOLDING_CEILING}. All holdings remain visible so the component does not hide a fact.`
    );
  }

  return (
    <section
      {...sectionProps}
      className={["apf-absence-block", className].filter(Boolean).join(" ")}
      aria-labelledby={titleId}
    >
      <p className="apf-absence-block__eyebrow">{eyebrow}</p>
      <Heading className="apf-absence-block__title" id={titleId}>{title}</Heading>
      <p className="apf-absence-block__support">{support}</p>
      {readings.length ? (
        <dl className="apf-absence-block__holdings">
          {readings.map((reading, index) => (
            <div className="apf-absence-block__holding" key={index}>
              <dt className="apf-absence-block__label">{reading.label}</dt>
              <dd className="apf-absence-block__count">{reading.count}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {routes ? <div className="apf-absence-block__routes">{routes}</div> : null}
    </section>
  );
}

export function LoadingState() {
  return (
    <div className="apf-loading-state" role="status" aria-live="polite">
      <div className="apf-state-message">
        <h2>Loading Accenture GroundWorks…</h2>
        <div className="apf-loading-state__bar" aria-hidden="true" />
      </div>
    </div>
  );
}

export function ArrowLinkLabel({ children }: { children: ReactNode }) {
  return (
    <span className="apf-arrow-link-label">
      <span>{children}</span>
      <Glyph meaning="utility.arrow-right" size={16} />
    </span>
  );
}

/** Two-digit index ordinal, drawn in mono, taken from the config order of a list. */
export function ordinal(position: number): string {
  return String(position).padStart(2, "0");
}

/* ------------------------------------------------------------------ */
/* The universal kit (calm redesign). One container family, two        */
/* registers. Every string renders from props, never from this file.   */
/* ------------------------------------------------------------------ */

/**
 * Lockup: the framework's identity, drawn once and used everywhere it
 * appears. The datum mark, the name and the descriptor line.
 *
 * The two strings are fixed by the naming law: the framework is Accenture
 * GroundWorks and its descriptor is "The foundation for agentic source-to-pay."
 * They resolve through the copy layer so a partner profile can carry its own
 * pair, and the governed wording is the fallback, so the lockup is never blank
 * and never off-name while a key is being added.
 *
 * The mark is decoration here: it repeats the name beside it, and the header
 * link states its own accessible name, so nothing is carried by the image
 * alone.
 */
/* The GroundWorks mark, adopted from groundworks-v0.9.0: the ground is a
 * three-by-three block at the origin, the work a two-by-two block in the far
 * corner, overlapping on exactly one module - the datum, the only anchor
 * value in the mark. Ground and work take the text ink, so the mark themes
 * with the header; the datum takes the accent role, which rests on the
 * upstream anchor-mark value in both themes. */
export function GroundworksMark({ size = 28 }: { size?: number }) {
  return (
    <svg className="apf-groundworks-mark" width={size} height={size} viewBox="0 0 32 32" aria-hidden="true" focusable="false">
      <rect className="apf-groundworks-mark__ground" x="0" y="0" width="24" height="24" />
      <rect className="apf-groundworks-mark__work" x="16" y="16" width="16" height="16" />
      <rect className="apf-groundworks-mark__datum" x="16" y="16" width="8" height="8" />
    </svg>
  );
}

export function Lockup({ compact = false, mark = false, nameKey = "brand.name", fallbackName = "Accenture GroundWorks" }: {
  /** Mark and name on one line, descriptor dropped. The home identity plane. */
  compact?: boolean;
  /** The header lockup carries the GroundWorks mark; other lockups stay text. */
  mark?: boolean;
  /** The site header renders the short program name (operator decision,
   * 13 August 2026); every other lockup keeps the full brand name. */
  nameKey?: string;
  fallbackName?: string;
}) {
  const copy = useCopy();
  return (
    <span className={`apf-lockup${compact ? " apf-lockup--compact" : ""}${mark ? " apf-lockup--marked" : ""}`}>
      {mark ? <GroundworksMark /> : null}
      <span className="apf-lockup__name">{copy(nameKey, fallbackName)}</span>
      {compact ? null : (
        <span className="apf-lockup__descriptor">
          {copy("brand.descriptor", "The foundation for agentic source-to-pay.")}
        </span>
      )}
    </span>
  );
}

/** One scope segment in a place line. */
export type PlaceSegment = {
  /** The words, in sentence case ("45 in catalogue"). */
  text: string;
};

/**
 * One place-line part, resolved through the copy layer and nothing else.
 *
 * The copy layer is the single source for these strings, so a key an audience
 * does not carry resolves to nothing rather than to its own key text or to a
 * literal written into a page. Three pages each invented their own answer to
 * this and each said something different; the line now renders what the
 * snapshot gives it, and drops the part it was not given.
 */
export function placePart(copy: (key: string, fallback?: string) => string, key: string): string {
  const value = copy(key);
  return value === key ? "" : value;
}

/**
 * Place line: the quiet row under a page header on a technical page, saying
 * where the reader is standing. The section word, what is in scope, and one
 * version or data cut.
 *
 * The line reads as words, so its segments set in sans; the cut is a version
 * or a timestamp, which is a machine value, so it keeps the machine voice. It
 * carries no link and no accent: it states a position, it does not offer one.
 * A place line with nothing to say renders nothing, rather than a rule with an
 * empty row under it.
 */
export function PlaceLine({ section, segments = [], cut }: {
  /** The section word, in the reader's vocabulary ("Workblocks"). */
  section: string;
  /** What is in scope, one short phrase each. */
  segments?: PlaceSegment[];
  /** One version, registry stamp or data cut. Machine value, drawn in mono. */
  cut?: string;
}) {
  const shown = segments.filter((segment) => segment.text.trim().length > 0);
  if (!section.trim() && !shown.length && !cut) return null;
  return (
    <p className="apf-place-line">
      {section.trim() ? <span className="apf-place-line__section">{section}</span> : null}
      {shown.map((segment) => (
        <span className="apf-place-line__segment" key={segment.text}>
          {segment.text}
        </span>
      ))}
      {cut ? <span className="apf-place-line__cut">{cut}</span> : null}
    </p>
  );
}

/** One label and reading pair in a fact band. */
export type BandFact = {
  label: string;
  /** A count, a version or an identifier. Machine value, drawn in mono. */
  reading: string | number;
  /** One short line of context, where the reading needs one. */
  note?: string;
};

/**
 * Fact band: one ruled row of label and reading pairs. It replaces every grid
 * of bordered stat tiles, which said the same readings eight times over and
 * made a reading look like a control.
 *
 * Zero readings are dropped at this API, for the same reason FactRow drops
 * them: "0 reference builds" is a defect, not a fact. A band with nothing left
 * renders nothing.
 */
export function FactBand({ label, facts }: {
  /** Accessible name for the band, naming what the readings count. */
  label: string;
  facts: BandFact[];
}) {
  const shown = facts.filter((fact) => !isZeroFact(fact.reading));
  if (!shown.length) return null;
  return (
    <ul className="apf-fact-band" aria-label={label}>
      {shown.map((fact) => (
        <li className="apf-fact-band__item" key={fact.label}>
          <span className="apf-fact-band__label">{fact.label}</span>
          <span className="apf-fact-band__reading">{fact.reading}</span>
          {fact.note ? <span className="apf-fact-band__note">{fact.note}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * One onward step in a Trail. `context` is the one-line answer to "why would
 * I go there" and always renders under the label.
 */
export type TrailItem = {
  to: string;
  /** Content-class glyph beside the label, never instead of it. */
  glyph?: GlyphMeaning;
  label: string;
  context: string;
};

/**
 * Trail: the uniform onward block that closes every marketing page and major
 * section. Two to four glyph-labelled links, each with a one-line context.
 * One anatomy everywhere; it replaces every ad-hoc onward link line. The
 * component enforces the two-to-four contract at the API: a fifth item is
 * dropped, and a lone item renders nothing (a trail of one is a link line,
 * not a trail: put the link in the prose instead).
 */
export function Trail({ label, items }: { label: string; items: TrailItem[] }) {
  const shown = items.slice(0, 4);
  if (shown.length < 2) return null;
  return (
    <nav className="apf-trail" aria-label={label}>
      <ul className="apf-trail__list">
        {shown.map((item) => (
          <li className="apf-trail__item" key={`${item.to}:${item.label}`}>
            <Link className="apf-trail__link" to={item.to}>
              <span className="apf-trail__label">
                {item.glyph ? <Glyph meaning={item.glyph} size={16} className="apf-lead-glyph" /> : null}
                <span className="apf-trail__label-text">{item.label}</span>
                <Glyph meaning="utility.arrow-right" size={16} className="apf-trail__arrow" />
              </span>
              <span className="apf-trail__context">{item.context}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * The mono law inside one phrase. A reading like "9 systems of work" is one
 * figure and four words: the figure is the machine value and takes the machine
 * face, the words are words and stay in the reading face. The governed string
 * arrives whole and is marked here, never rewritten, so the copy layer stays
 * the only place the sentence is decided.
 */
export function markFigures(text: string, figureClass: string): ReactNode[] {
  return text
    .split(/(\d+(?:[.,]\d+)*)/)
    .map((part, index) =>
      /^\d/.test(part) ? (
        <span className={figureClass} key={`figure-${index}`}>
          {part}
        </span>
      ) : (
        part
      )
    );
}

/**
 * Door: one destination in a ruled list of intent doors. A door is a row, not
 * a card: an ordinal, a glyph, the job-phrased title, one line, an optional
 * count and one arrow, with the whole row as the link. Doors sit in an
 * `apf-door-list`, which draws the single hairline container around the set.
 * The row carries no accent, because the page's anchor budget is one composed
 * plane: a purple label repeated down a list is inflation, and the row is
 * already the target.
 */
export function Door({ to, glyph, index, title, line, meta }: {
  to: string;
  glyph: GlyphMeaning;
  /** Per-view ordinal, already formatted ("01"). Machine value, drawn in mono. */
  index?: string;
  title: string;
  line: string;
  /** One machine reading for the destination ("5 Workblocks"), in quiet ink. */
  meta?: string;
}) {
  return (
    <Link className="apf-door" to={to}>
      {index ? <span className="apf-door__ordinal">{index}</span> : null}
      <Glyph meaning={glyph} size={20} className="apf-door__glyph" />
      <span className="apf-door__title">{title}</span>
      <span className="apf-door__line">{line}</span>
      {meta ? <span className="apf-door__meta">{markFigures(meta, "apf-door__figure")}</span> : null}
      <span className="apf-door__arrow" aria-hidden="true">
        <Glyph meaning="utility.arrow-right" size={16} />
      </span>
    </Link>
  );
}

/**
 * Card (marketing register): glyph, name, an optional sentence-case
 * descriptor line, a short explainer beneath, and one count-plus-arrow
 * action. One anatomy for every marketing card grid; the domains index
 * draws nine of these. The descriptor is a line of prose in sentence case,
 * never a wrapped uppercase kicker, and the ordinal is per-view numbering
 * drawn in mono.
 */
export function Card({ to, glyph, index, name, descriptor, explainer, action }: {
  to: string;
  /** Content-class glyph in the card's top corner, never instead of the name. */
  glyph?: GlyphMeaning;
  /** Per-view ordinal, already formatted ("01"). */
  index?: string;
  name: string;
  descriptor?: string;
  explainer?: string;
  /** The one action label, rendered with the arrow ("5 Workblocks"). */
  action: string;
}) {
  return (
    <li className="apf-card-cell">
      <Link className="apf-frame apf-card" to={to}>
        <span className="apf-card__top">
          {glyph ? <Glyph meaning={glyph} size={20} className="apf-card__glyph" /> : null}
          {index ? <span className="apf-card__ordinal">{index}</span> : null}
        </span>
        <h2 className="apf-card__name">{name}</h2>
        {descriptor ? <p className="apf-card__descriptor">{descriptor}</p> : null}
        {explainer ? <p className="apf-card__explainer">{explainer}</p> : null}
        <span className="apf-card__meta">
          <ArrowLinkLabel>{action}</ArrowLinkLabel>
        </span>
      </Link>
    </li>
  );
}

/**
 * Statement section (marketing register): eyebrow, one measured headline,
 * then a single column at the prose measure. It replaces three-column prose,
 * kicker pairs and floating display questions. `inverse` seats the section on
 * the statement plane, one of the container family's two sanctioned
 * variants, and at most one per page.
 */
type StatementSectionProps = {
  id?: string;
  eyebrow: string;
  headline: string;
  /** Lets the column own the section width for figure sequences such as the
   * before/after pairs; prose sections keep the register measure. */
  wide?: boolean;
  children?: ReactNode;
} & (
  | { inverse?: false; treatment?: "solid" }
  | { inverse: true; treatment?: "solid" | "gradient" }
);

export function StatementSection({
  id,
  eyebrow,
  headline,
  inverse = false,
  treatment = "solid",
  wide = false,
  children
}: StatementSectionProps) {
  const className = [
    "apf-page-section",
    "apf-statement-section",
    inverse ? "apf-statement-section--inverse" : "",
    inverse && treatment === "gradient" ? "apf-statement-section--gradient" : "",
    wide ? "apf-statement-section--wide" : ""
  ].filter(Boolean).join(" ");
  return (
    <section className={className} id={id}>
      <p className="apf-statement-section__eyebrow">{eyebrow}</p>
      <h2 className="apf-statement-section__headline">{headline}</h2>
      {children ? <div className="apf-statement-section__column">{children}</div> : null}
    </section>
  );
}

/**
 * Before/after pair: the primary AI-first storytelling device, titled by the
 * mechanism. The two kicker labels arrive as copy from the caller, so the
 * same anatomy carries "Before/After" on domain pages and the legacy-model /
 * method positions on the home page.
 */
export function BeforeAfter({ title, beforeLabel, afterLabel, before, after }: {
  title?: string;
  beforeLabel: string;
  afterLabel: string;
  before: ReactNode;
  after: ReactNode;
}) {
  // One sanctioned move: on first scroll into view the after pane fades in a
  // beat behind the before pane. Plays once; still under reduced motion.
  const reveal = useRevealOnce<HTMLElement>();
  return (
    <article className={`apf-before-after${reveal.revealClass}`} ref={reveal.ref}>
      {title ? <h3>{title}</h3> : null}
      <div className="apf-before-after__pair">
        <div className="apf-before-after__state">
          <p className="apf-before-after__kicker">{beforeLabel}</p>
          <p>{before}</p>
        </div>
        <div className="apf-before-after__state">
          <p className="apf-before-after__kicker">{afterLabel}</p>
          <p>{after}</p>
        </div>
      </div>
    </article>
  );
}

/**
 * Editorial figure: a table or fact list set inside a marketing read. It
 * shares the prose type family and measure grid, and the optional lead-in
 * line renders only where a caption earns its place, never chrome dropped
 * into a story.
 */
export function Figure({ lead, children }: { lead?: string; children: ReactNode }) {
  return (
    <figure className="apf-figure">
      {lead ? <figcaption className="apf-figure__lead">{lead}</figcaption> : null}
      <div className="apf-figure__body">{children}</div>
    </figure>
  );
}

/** One fact in a hero or figure fact row. */
export type FactItem = {
  label: string;
  value: string | number;
  /** Optional destination: the fact becomes a quiet link. */
  to?: string;
};

/**
 * A zero stat never renders: "0 reference builds" is a defect, not a fact.
 * A fact whose value is 0, or whose leading number is 0, is dropped here so
 * no caller can ship one by accident. Recast the sentence instead
 * ("5 Workblocks · All blueprints").
 */
function isZeroFact(value: string | number): boolean {
  if (typeof value === "number") return value === 0;
  const trimmed = value.trim();
  if (!trimmed) return true;
  const leadingNumber = trimmed.match(/^\d+/);
  return leadingNumber !== null && Number(leadingNumber[0]) === 0;
}

/**
 * Fact row: up to three label/value facts under a hero or inside a figure.
 * Zero and empty facts are filtered at this API so they cannot render.
 */
export function FactRow({ facts }: { facts: FactItem[] }) {
  const shown = facts.filter((fact) => !isZeroFact(fact.value)).slice(0, 3);
  if (!shown.length) return null;
  return (
    <ul className="apf-fact-row">
      {shown.map((fact) => (
        <li className="apf-fact-row__item" key={fact.label}>
          {fact.to ? (
            <Link className="apf-fact-row__link" to={fact.to}>
              <span className="apf-fact-row__value">{fact.value}</span>
              <span className="apf-fact-row__label">{fact.label}</span>
            </Link>
          ) : (
            <>
              <span className="apf-fact-row__value">{fact.value}</span>
              <span className="apf-fact-row__label">{fact.label}</span>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
